import { get, post, del } from './apiClient';
import { ApiResponse } from './tenantApi';

// Type definitions
export interface EmailDomain {
    name: string;
    status: string;
    verificationStatus: string;
}

export interface DomainVerificationStatus {
    domain: string;
    status: string;
    verificationStatus: string;
    dkimVerificationStatus: string;
    spfVerificationStatus: string;
    mxVerificationStatus: string;
}

export interface VirtualMachine {
    name: string;
    id: string;
    location: string;
    status: string;
}

export interface AppService {
    name: string;
    id: string;
    defaultHostName: string;
    state: string;
}

export interface DnsRecord {
    type: string;
    name: string;
    data: string;
    ttl: number;
    priority?: number;
}

export interface GoDaddyDomain {
    domain: string;
    status: string;
    expirationDate: string;
    renewable: boolean;
}

export interface EmailConfig {
    mxRecords?: {
        name: string;
        value: string;
        priority?: number;
    }[];
    txtRecords?: {
        name: string;
        value: string;
    }[];
    cnameRecords?: {
        name: string;
        value: string;
    }[];
}

export interface SubdomainConfig {
    parentDomain: string;
    subdomain: string;
    dnsData: {
        cnameRecords: {
            name: string;
            value: string;
        }[];
    };
}

/**
 * Get all email domains
 * @returns Promise with email domains array
 */
export const getAllEmailDomains = (): Promise<ApiResponse<EmailDomain[]>> => {
    return get<ApiResponse<EmailDomain[]>>('/domains/email');
};

/**
 * Add new email domain
 * @param domain Domain to add
 * @returns Promise with domain setup information
 */
export const addEmailDomain = (domain: string): Promise<ApiResponse<any>> => {
    return post<ApiResponse<any>>('/domains/email', { domain });
};

/**
 * Check email domain verification status
 * @param domain Domain to check
 * @returns Promise with verification status
 */
export const checkEmailDomainStatus = (domain: string): Promise<ApiResponse<DomainVerificationStatus>> => {
    return get<ApiResponse<DomainVerificationStatus>>(`/domains/email/${domain}/status`);
};

/**
 * Delete email domain
 * @param domain Domain to delete
 * @returns Promise with deletion result
 */
export const deleteEmailDomain = (domain: string): Promise<ApiResponse<any>> => {
    return del<ApiResponse<any>>(`/domains/email/${domain}`);
};

/**
 * Get all VMs
 * @returns Promise with VMs array
 */
export const getAllVMs = (): Promise<ApiResponse<VirtualMachine[]>> => {
    return get<ApiResponse<VirtualMachine[]>>('/domains/vms');
};

/**
 * Get all App Services
 * @returns Promise with App Services array
 */
export const getAllAppServices = (): Promise<ApiResponse<AppService[]>> => {
    return get<ApiResponse<AppService[]>>('/domains/app-services');
};

/**
 * Add custom domain to App Service or VM
 * @param serviceName Service name
 * @param domain Domain to add
 * @param isAppService Whether it's an App Service
 * @returns Promise with domain addition result
 */
export const addCustomDomain = (
    serviceName: string,
    domain: string,
    isAppService: boolean = true
): Promise<ApiResponse<any>> => {
    return post<ApiResponse<any>>('/domains/services', {
        serviceName,
        domain,
        isAppService
    });
};

/**
 * Remove custom domain from App Service or VM
 * @param serviceName Service name
 * @param domain Domain to remove
 * @param isAppService Whether it's an App Service
 * @returns Promise with domain removal result
 */
export const removeCustomDomain = (
    serviceName: string,
    domain: string,
    isAppService: boolean = true
): Promise<ApiResponse<any>> => {
    return del<ApiResponse<any>>('/domains/services', {
        data: {
            serviceName,
            domain,
            isAppService
        }
    });
};

/**
 * Get all GoDaddy domains
 * @returns Promise with domains array
 */
export const getAllGoDaddyDomains = (): Promise<ApiResponse<GoDaddyDomain[]>> => {
    return get<ApiResponse<GoDaddyDomain[]>>('/domains/godaddy');
};

/**
 * Get DNS records for a domain
 * @param domain Domain to get records for
 * @param type Optional record type filter
 * @param name Optional record name filter
 * @returns Promise with DNS records array
 */
export const getDnsRecords = (
    domain: string,
    type?: string,
    name?: string
): Promise<ApiResponse<DnsRecord[]>> => {
    let url = `/domains/dns/${domain}/records`;
    if (type) {
        url += `/${type}`;
        if (name) {
            url += `/${name}`;
        }
    }
    return get<ApiResponse<DnsRecord[]>>(url);
};

/**
 * Add DNS record
 * @param domain Domain to update
 * @param record Record to add
 * @returns Promise with addition result
 */
export const addDnsRecord = (
    domain: string,
    record: DnsRecord
): Promise<ApiResponse<any>> => {
    return post<ApiResponse<any>>(`/domains/dns/${domain}/records`, record);
};

/**
 * Delete DNS record
 * @param domain Domain to update
 * @param type Record type
 * @param name Record name
 * @returns Promise with deletion result
 */
export const deleteDnsRecord = (
    domain: string,
    type: string,
    name: string
): Promise<ApiResponse<any>> => {
    return del<ApiResponse<any>>(`/domains/dns/${domain}/records/${type}/${name}`);
};

/**
 * Configure email DNS settings
 * @param domain Domain to configure
 * @param emailConfig Email configuration
 * @returns Promise with configuration result
 */
export const configureEmailDns = (
    domain: string,
    emailConfig: EmailConfig
): Promise<ApiResponse<any>> => {
    return post<ApiResponse<any>>(`/domains/dns/${domain}/email-config`, emailConfig);
};

/**
 * Create a new subdomain with CNAME records for ontoris.com
 * This will create both the subdomain CNAME and hub.subdomain CNAME
 * @param config Configuration for the subdomain
 * @returns Promise with creation result
 */
export const createSubdomain = (
    config: SubdomainConfig
): Promise<ApiResponse<any>> => {
    // Validate that parent domain is ontoris.com
    if (config.parentDomain !== 'ontoris.com') {
        return Promise.reject({
            success: false,
            message: 'Subdomain creation is only supported for ontoris.com'
        });
    }

    return post<ApiResponse<any>>('/domains/subdomains', {
         ...config 
    });
};

/**
 * Get all subdomains for a specific domain
 * @param domain Parent domain
 * @returns Promise with subdomains list
 */
export const getSubdomains = (
    domain: string
): Promise<ApiResponse<string[]>> => {
    return get<ApiResponse<string[]>>(`/domains/dns/${domain}/subdomains`);
};